import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ClipboardList, CheckCircle2, Loader2, RefreshCw, AlertTriangle, ChevronRight, Clock, UserPlus, PackageCheck, Search, RotateCcw } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from "../ui/dropdown-menu";
import DealApprovalReviewModal from "./DealApprovalReviewModal";

/**
 * Jun-2026 (User-Wunsch) — „Meine Aufgaben" für Mitarbeiter (Zweiradverkäufer).
 * Zeigt die vom Händler zugewiesenen offenen Aufgaben mit „Erledigt"-Button.
 * Überfällige Aufgaben (> 3 Tage) werden markiert.
 */
export default function MyTasks() {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [employees, setEmployees] = useState([]);
    const [storeLoc, setStoreLoc] = useState({});  // taskId → gewählter location_id / Freitext
    const [locations, setLocations] = useState([]); // Lagerplätze des Standorts
    // Jul-2026 (User-Wunsch) — Suche & Filter, Erledigt-Ansicht + Reaktivieren
    const [view, setView] = useState("open");
    const [search, setSearch] = useState("");
    const [areaFilter, setAreaFilter] = useState("");
    const [counts, setCounts] = useState({ open: 0, done: 0 });
    // Jul-2026 (User-Wunsch) — Aufgaben in 10/20/50er-Blöcken anzeigen.
    const [page, setPage] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    // Aug-2026 (User-Wunsch) — Prüf-Freigabe: Gesamt-PDF vor Freigabe/Ablehnung.
    const [reviewTask, setReviewTask] = useState(null);
    useEffect(() => { setPage(0); }, [view, search, areaFilter]);
    const navigate = useNavigate();

    const load = async (which = view) => {
        setLoading(true);
        try {
            const r = await api.get(`/me/tasks?status=${which}`);
            setItems(r.data.items || []);
            setCounts({ open: r.data.open_count || 0, done: r.data.done_count || 0 });
        } catch {
            /* ignore */
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { const t = setTimeout(() => load(view), 0); return () => clearTimeout(t); }, [view]); // eslint-disable-line react-hooks/exhaustive-deps

    // Phase 4e — Lagerplätze des Standorts für die Einlagerungs-Auswahl laden.
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(() => {
            api.get("/dealer/storage-locations")
                .then((r) => { if (!cancelled) setLocations(Array.isArray(r.data) ? r.data : (r.data?.items || [])); })
                .catch(() => { /* optional */ });
        }, 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, []);

    // Jun-2026 (User-Wunsch) — zuweisbare Kollegen/Mitarbeiter für „Zuweisen".
    useEffect(() => {
        let cancelled = false;
        const t = setTimeout(() => {
            api.get("/me/tasks/assignable-employees")
                .then((r) => { if (!cancelled) setEmployees(r.data?.items || []); })
                .catch(() => { /* ignore */ });
        }, 0);
        return () => { cancelled = true; clearTimeout(t); };
    }, []);

    const complete = async (id) => {
        try {
            await api.post(`/me/tasks/${id}/done`);
            toast.success("Aufgabe als erledigt gemeldet");
            await load();
        } catch {
            toast.error("Konnte nicht als erledigt markiert werden");
        }
    };

    // Jul-2026 (User-Wunsch) — versehentlich erledigte Aufgabe wieder aktivieren.
    const reopen = async (id) => {
        try {
            await api.post(`/me/tasks/${id}/reopen`);
            toast.success("Aufgabe wieder aktiviert");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Reaktivieren fehlgeschlagen");
        }
    };

    // Phase 4 — 1-Klick „Erledigt & eingelagert": bucht den Lagerort am
    // Zielstandort und schließt die „Wareneingang prüfen"-Aufgabe.
    const storeAndComplete = async (t) => {
        const val = (storeLoc[t.id] ?? (t.suggested_location_id || "")).toString().trim();
        const hasLocations = locations.length > 0;
        const payload = hasLocations ? { location_id: val || null } : { location: val || null };
        try {
            const { data } = await api.post(`/org/transfers/${t.transfer_id}/store`, payload);
            const label = data.location || "";
            toast.success(label ? `Eingelagert in ${label} — Aufgabe erledigt` : "Eingelagert — Aufgabe erledigt");
            setStoreLoc((s) => { const n = { ...s }; delete n[t.id]; return n; });
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Einlagern fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Aufgabe schlummern lassen (1 Std / 3 Std / 1 Tag /
    // 3 Tage): wird ausgeblendet und nach Ablauf der Zeit erneut vorgelegt.
    const snooze = async (id, hours, label) => {
        try {
            await api.post(`/me/tasks/${id}/snooze`, { hours });
            toast.success(`Aufgabe geschlummert — wird in ${label} erneut vorgelegt`);
            await load();
        } catch {
            toast.error("Schlummern fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Aufgabe an einen (anderen) Mitarbeiter zuweisen.
    const reassign = async (id, assigneeId, name) => {
        try {
            // Jun-2026 (User-Wunsch) — bei jeder Zuweisung eine Info an den Mitarbeiter mitgeben
            const note = window.prompt(`Information für ${name || "den Mitarbeiter"} (optional):`, "") || "";
            await api.post(`/me/tasks/${id}/reassign`, { assignee_id: assigneeId, note: note.trim() || null });
            toast.success(`Aufgabe an ${name} zugewiesen`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Zuweisen fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — 1-Klick-Freigabe für Gesamtgeschäft direkt aus der Liste.
    const approveDeal = async (id, sourceType) => {
        try {
            await api.post(`/me/tasks/${id}/approve-deal`);
            toast.success(sourceType === "discount_approval_pending" ? "Rabatt freigegeben"
                : sourceType === "tradein_approval_pending" ? "Ankauf freigegeben" : "Gesamtgeschäft freigegeben");
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Freigabe fehlgeschlagen");
        }
    };

    // Jun-2026 (User-Wunsch) — Sammel-Freigabe aller offenen Gesamtgeschäfte.
    // Jun-2026 (User-Wunsch) — alle offenen Aufgaben mit einem Klick erledigen (Test-Reset).
    const completeAll = async () => {
        if (!window.confirm(`Wirklich ALLE ${counts.open} offenen Aufgaben als erledigt markieren?\n\nErledigte Aufgaben lassen sich einzeln wieder aktivieren.`)) return;
        try {
            const r = await api.post("/me/tasks/complete-all");
            toast.success(`${r.data.completed} Aufgabe(n) als erledigt markiert`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Aktion fehlgeschlagen");
        }
    };

    const approveAllDeals = async () => {
        try {
            const r = await api.post("/me/tasks/approve-all-deals");
            toast.success(`${r.data?.approved ?? 0} Gesamtgeschäft(e) freigegeben`);
            await load();
        } catch (e) {
            toast.error(e.response?.data?.detail || "Sammel-Freigabe fehlgeschlagen");
        }
    };
    const openDealCount = items.filter((t) => ["deal_approval_pending", "tradein_approval_pending"].includes(t.source_type)).length;

    // Jul-2026 (User-Wunsch) — Suche & Bereichs-Filter (clientseitig)
    const areas = [...new Set(items.map((t) => t.area).filter(Boolean))].sort();
    const needle = search.trim().toLowerCase();
    const filtered = items.filter((t) => {
        if (areaFilter && t.area !== areaFilter) return false;
        if (!needle) return true;
        return [t.title, t.body, t.area, t.assignee_name].some((v) => (v || "").toLowerCase().includes(needle));
    });
    const totalTasks = counts.open + counts.done;
    const donePct = totalTasks ? Math.round((counts.done / totalTasks) * 100) : 0;
    const pages = Math.max(1, Math.ceil(filtered.length / pageSize));
    const safePage = Math.min(page, pages - 1);
    const paged = filtered.slice(safePage * pageSize, (safePage + 1) * pageSize);

    if (loading && items.length === 0 && !search && !areaFilter) {
        return <div className="bg-white border border-gray-200 rounded-sm p-6 flex items-center gap-2 text-gray-500"><Loader2 className="w-4 h-4 animate-spin" /> Lade Aufgaben…</div>;
    }
    if (totalTasks === 0) return null;

    return (
        <div className="bg-white border-2 border-[#FFCC00] rounded-sm p-5 mb-6" data-testid="my-tasks">
            <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                    <ClipboardList className="w-5 h-5 text-[#111827]" />
                    <h2 className="font-display font-black text-lg">Meine Aufgaben</h2>
                    <span className="text-[10px] font-bold uppercase tracking-wider bg-[#111827] text-[#FFCC00] px-2 py-0.5 rounded-sm font-mono-num">{counts.open}</span>
                </div>
                <div className="flex items-center gap-3">
                    {view === "open" && counts.open >= 2 && (
                        <button onClick={completeAll} className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-emerald-700 inline-flex items-center gap-1" title="Alle offenen Aufgaben als erledigt markieren" data-testid="my-tasks-complete-all">
                            <CheckCircle2 className="w-3 h-3" /> Alle erledigen
                        </button>
                    )}
                    <button onClick={() => load()} className="text-xs font-bold uppercase tracking-wider text-gray-500 hover:text-[#111827] inline-flex items-center gap-1" data-testid="my-tasks-refresh">
                        <RefreshCw className="w-3 h-3" /> Aktualisieren
                    </button>
                </div>
            </div>
            {/* Jul-2026 (User-Wunsch) — Fortschritt, Status-Umschalter, Suche & Bereichs-Filter */}
            <div className="mb-3 space-y-2">
                <div className="flex items-center gap-2" data-testid="my-tasks-progress">
                    <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                        <div className="h-full bg-emerald-600" style={{ width: `${donePct}%` }} />
                    </div>
                    <span className="text-[10px] font-bold text-gray-500 font-mono-num shrink-0">{counts.done}/{totalTasks} erledigt ({donePct} %)</span>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="inline-flex rounded-sm border border-gray-300 overflow-hidden">
                        <button type="button" onClick={() => setView("open")} className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${view === "open" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:text-[#111827]"}`} data-testid="my-tasks-view-open">
                            Offen ({counts.open})
                        </button>
                        <button type="button" onClick={() => setView("done")} className={`px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider ${view === "done" ? "bg-[#111827] text-white" : "bg-white text-gray-600 hover:text-[#111827]"}`} data-testid="my-tasks-view-done">
                            Erledigt ({counts.done})
                        </button>
                    </div>
                    <div className="relative flex-1 min-w-[140px]">
                        <Search className="w-3.5 h-3.5 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2 pointer-events-none" />
                        <input
                            type="text" value={search} onChange={(e) => setSearch(e.target.value)}
                            placeholder="Aufgaben durchsuchen…"
                            className="w-full h-7 pl-7 pr-2 text-xs border border-gray-300 rounded-sm bg-white"
                            data-testid="my-tasks-search"
                        />
                    </div>
                    {areas.length > 1 && (
                        <select value={areaFilter} onChange={(e) => setAreaFilter(e.target.value)}
                            className="h-7 text-[11px] px-1.5 border border-gray-300 rounded-sm bg-white" data-testid="my-tasks-area-filter" aria-label="Bereich filtern">
                            <option value="">Alle Bereiche</option>
                            {areas.map((a) => <option key={a} value={a}>{a}</option>)}
                        </select>
                    )}
                </div>
            </div>
            {filtered.length === 0 && (
                <p className="text-xs text-gray-500 py-2" data-testid="my-tasks-empty-filtered">
                    {view === "done" ? "Keine erledigten Aufgaben" : "Keine Aufgaben"}{needle || areaFilter ? " für diesen Filter." : "."}
                </p>
            )}
            {view === "open" && openDealCount >= 2 && (
                <button type="button" onClick={approveAllDeals} className="mb-3 w-full inline-flex items-center justify-center gap-1.5 text-xs font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-3 py-2" data-testid="my-tasks-approve-all-deals">
                    <CheckCircle2 className="w-4 h-4" /> Alle Freigaben erteilen ({openDealCount})
                </button>
            )}
            {filtered.length > 0 && (
                <div className="mb-3 flex items-center gap-2 flex-wrap" data-testid="my-tasks-pagination-controls">
                    <span className="text-[10px] text-gray-500 uppercase tracking-wider font-bold">Pro Seite:</span>
                    {[10, 20, 50].map((n) => (
                        <button
                            key={n}
                            type="button"
                            onClick={() => { setPageSize(n); setPage(0); }}
                            className={`text-[10px] font-bold px-2 py-0.5 rounded-sm border ${pageSize === n ? "bg-[#111827] text-[#FFCC00] border-[#111827]" : "bg-white text-gray-700 border-gray-300 hover:bg-gray-50"}`}
                            data-testid={`my-tasks-pagesize-${n}`}
                        >
                            {n}
                        </button>
                    ))}
                    <span className="text-[10px] text-gray-500 font-mono-num ml-1" data-testid="my-tasks-page-info">
                        {safePage * pageSize + 1}–{Math.min((safePage + 1) * pageSize, filtered.length)} von {filtered.length}
                    </span>
                    {pages > 1 && (
                        <span className="inline-flex items-center gap-1">
                            <button
                                type="button"
                                disabled={safePage === 0}
                                onClick={() => setPage(safePage - 1)}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40"
                                data-testid="my-tasks-page-prev"
                            >
                                ‹
                            </button>
                            <button
                                type="button"
                                disabled={safePage >= pages - 1}
                                onClick={() => setPage(safePage + 1)}
                                className="text-[10px] font-bold px-2 py-0.5 rounded-sm border border-gray-300 bg-white disabled:opacity-40"
                                data-testid="my-tasks-page-next"
                            >
                                ›
                            </button>
                        </span>
                    )}
                </div>
            )}
            <ul className="space-y-2">
                {paged.map((t) => (
                    <li key={t.id} className={`border rounded-sm px-3 py-2.5 ${t.status === "done" ? "border-emerald-200 bg-emerald-50/40" : t.overdue ? "border-red-300 bg-red-50" : "border-gray-200 bg-white"}`} data-testid={`my-task-${t.id}`}>
                        <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-[#FFCC00] text-[#111827]">{t.area}</span>
                            {t.overdue && (
                                <span className="inline-flex items-center gap-1 text-[9px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded-sm bg-red-600 text-white" data-testid={`my-task-overdue-${t.id}`}>
                                    <AlertTriangle className="w-3 h-3" /> Überfällig
                                </span>
                            )}
                            <span className="font-bold text-sm text-gray-900">{t.title}</span>
                            {t.grouped_count > 1 && (
                                <span className="inline-flex items-center text-[9px] font-bold px-1.5 py-0.5 rounded-sm bg-blue-100 text-blue-800 border border-blue-200"
                                    title={`${t.grouped_count} Erinnerungen zu diesem Lead zusammengefasst — eine Bearbeitung gilt für alle`}
                                    data-testid={`my-task-grouped-${t.id}`}>
                                    {t.grouped_count}× zusammengefasst
                                </span>
                            )}
                            {t.created_at && (
                                <span className="text-[10px] font-bold text-gray-400 ml-auto shrink-0" title="Erstellt am"
                                    data-testid={`my-task-created-${t.id}`}>
                                    erstellt {new Date(t.created_at).toLocaleDateString("de-DE")}
                                </span>
                            )}
                        </div>
                        {t.body && <p className="text-xs text-gray-600 mt-0.5">{t.body}</p>}
                        {t.note && (
                            <p className="text-xs text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-sm px-2 py-1 mt-1" data-testid={`my-task-note-${t.id}`}>
                                <span className="font-bold">Info:</span> {t.note}
                            </p>
                        )}
                        {t.status === "done" ? (
                            <div className="mt-2 flex items-center gap-2 flex-wrap">
                                <span className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-700">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt{t.done_at ? ` am ${new Date(t.done_at).toLocaleDateString("de-DE")}` : ""}
                                </span>
                                <button
                                    type="button"
                                    onClick={() => reopen(t.id)}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-[#111827] border border-gray-400 hover:border-[#111827] rounded-sm px-2 py-1 bg-white"
                                    data-testid={`my-task-reopen-${t.id}`}
                                >
                                    <RotateCcw className="w-3.5 h-3.5" /> Wieder aktivieren
                                </button>
                            </div>
                        ) : (
                        <div className="mt-2 flex items-center gap-2 flex-wrap">
                            {t.source_type === "stock_transfer_received" && t.transfer_id && (
                                <div className="inline-flex items-center gap-1.5 bg-emerald-50 border border-emerald-200 rounded-sm px-1.5 py-1" data-testid={`my-task-store-form-${t.id}`}>
                                    {locations.length > 0 ? (
                                        <select
                                            value={storeLoc[t.id] ?? (t.suggested_location_id || "")}
                                            onChange={(e) => setStoreLoc((s) => ({ ...s, [t.id]: e.target.value }))}
                                            className="h-6 max-w-[160px] text-[11px] px-1 rounded-sm border border-emerald-300 bg-white"
                                            data-testid={`my-task-store-loc-${t.id}`} aria-label="Lagerplatz wählen"
                                        >
                                            <option value="">— Lagerplatz wählen —</option>
                                            {locations.map((l) => (
                                                <option key={l.id} value={l.id}>
                                                    {l.code}{l.name ? ` · ${l.name}` : ""}
                                                </option>
                                            ))}
                                        </select>
                                    ) : (
                                        <input
                                            type="text" placeholder="Regalplatz (z. B. A3)"
                                            value={storeLoc[t.id] ?? (t.suggested_location || "")}
                                            onChange={(e) => setStoreLoc((s) => ({ ...s, [t.id]: e.target.value }))}
                                            className="h-6 w-28 text-[11px] px-1.5 rounded-sm border border-emerald-300 bg-white"
                                            data-testid={`my-task-store-loc-${t.id}`}
                                        />
                                    )}
                                    <button
                                        type="button"
                                        onClick={() => storeAndComplete(t)}
                                        className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-2 py-1"
                                        data-testid={`my-task-store-${t.id}`}
                                    >
                                        <PackageCheck className="w-3.5 h-3.5" /> Erledigt & eingelagert
                                    </button>
                                </div>
                            )}
                            {["deal_approval_pending", "tradein_approval_pending", "discount_approval_pending"].includes(t.source_type) && (
                                <button
                                    type="button"
                                    onClick={() => (t.source_type === "deal_approval_pending" && t.listing_id)
                                        ? setReviewTask(t)
                                        : approveDeal(t.id, t.source_type)}
                                    className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-white bg-emerald-700 hover:bg-emerald-800 rounded-sm px-2.5 py-1"
                                    data-testid={`my-task-approve-${t.id}`}
                                >
                                    <CheckCircle2 className="w-3.5 h-3.5" /> {t.source_type === "deal_approval_pending" ? "Prüfen & Freigeben" : "Freigeben"}
                                </button>
                            )}
                            {t.cta_url && (
                                <button
                                    type="button"
                                    onClick={() => navigate(t.cta_url)}
                                    className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider rounded-sm px-2 py-1 ${t.source_type === "email_inbound" ? "bg-[#111827] text-white hover:bg-black border border-[#111827]" : "text-[#111827] hover:text-black border border-gray-300 bg-white"}`}
                                    data-testid={`my-task-open-${t.id}`}
                                >
                                    {t.source_type === "email_inbound" ? "Antworten (KI)" : "Öffnen"} <ChevronRight className="w-3 h-3" />
                                </button>
                            )}
                            <button
                                type="button"
                                onClick={() => complete(t.id)}
                                className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-700 hover:text-emerald-900 border border-emerald-300 rounded-sm px-2 py-1 bg-white"
                                data-testid={`my-task-done-${t.id}`}
                            >
                                <CheckCircle2 className="w-3.5 h-3.5" /> Erledigt
                            </button>
                            {/* Jun-2026 (User-Wunsch) — Schlummern (1 Std / 3 Std / 1 Tag / 3 Tage) */}
                            <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                    <button type="button" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white" data-testid={`my-task-snooze-${t.id}`}>
                                        <Clock className="w-3.5 h-3.5" /> Schlummern
                                    </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="min-w-[9rem]">
                                    <DropdownMenuItem onClick={() => snooze(t.id, 1, "1 Stunde")} data-testid={`my-task-snooze-1h-${t.id}`}>1 Stunde</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => snooze(t.id, 3, "3 Stunden")} data-testid={`my-task-snooze-3h-${t.id}`}>3 Stunden</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => snooze(t.id, 24, "1 Tag")} data-testid={`my-task-snooze-1d-${t.id}`}>1 Tag</DropdownMenuItem>
                                    <DropdownMenuItem onClick={() => snooze(t.id, 72, "3 Tage")} data-testid={`my-task-snooze-3d-${t.id}`}>3 Tage</DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                            {/* Jun-2026 (User-Wunsch) — Aufgabe an einen Mitarbeiter zuweisen */}
                            {employees.length > 0 && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <button type="button" className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-600 hover:text-[#111827] border border-gray-300 rounded-sm px-2 py-1 bg-white" data-testid={`my-task-assign-${t.id}`}>
                                            <UserPlus className="w-3.5 h-3.5" /> Zuweisen
                                        </button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="min-w-[11rem] max-h-72 overflow-y-auto">
                                        <DropdownMenuLabel className="text-[10px] uppercase tracking-wider text-gray-500">An Mitarbeiter zuweisen</DropdownMenuLabel>
                                        <DropdownMenuSeparator />
                                        {employees.map((e) => (
                                            <DropdownMenuItem
                                                key={e.id}
                                                onClick={() => reassign(t.id, e.id, e.name)}
                                                data-testid={`my-task-assign-${t.id}-${e.id}`}
                                            >
                                                {e.name}
                                                {e.employee_function && (
                                                    <span className="ml-1 text-[10px] text-gray-400">· {e.employee_function}</span>
                                                )}
                                            </DropdownMenuItem>
                                        ))}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                        )}
                    </li>
                ))}
            </ul>
            {reviewTask && (
                <DealApprovalReviewModal
                    task={reviewTask}
                    onClose={() => setReviewTask(null)}
                    onDone={() => { setReviewTask(null); load(); }}
                />
            )}
        </div>
    );
}
